import { useEffect, useMemo, useState, type JSX } from 'react';

interface Sky {
  src: string;
  tint: string;
  opacity: number;
  style: React.CSSProperties;
  drift?: boolean;
}

// 天气/时辰 → 一件水墨天象（低透明度，绝不抢戏）。
// 手机上播放卡占住画面上部, 天象压缩进卡片上方那条真正的天空带, 不被遮挡。
function pickSky(weather: string, hour: number, mobile: boolean): Sky {
  const w = weather.toLowerCase();
  const night = hour >= 19 || hour < 6;
  if (/rain|drizzle|shower|thunder|雨/.test(w))
    return {
      src: '/assets/sky-rain.png',
      tint: 'var(--faint)',
      opacity: 0.3,
      style: mobile ? { top: 0, left: 0, right: 0, height: '24vh' } : { top: 0, left: 0, right: 0, height: '52vh' },
    };
  if (/snow|sleet|雪/.test(w))
    return {
      src: '/assets/sky-snow.png',
      tint: 'var(--dim)',
      opacity: 0.38,
      style: mobile ? { top: 0, left: 0, right: 0, height: '22vh' } : { top: 0, left: 0, right: 0, height: '48vh' },
    };
  if (/cloud|overcast|mist|fog|haze|阴|云|雾/.test(w))
    return {
      src: '/assets/sky-mist.png',
      tint: 'var(--faint)',
      opacity: 0.42,
      style: mobile ? { top: '1vh', left: 0, right: 0, height: '16vh' } : { top: '4vh', left: 0, right: 0, height: '36vh' },
      drift: true,
    };
  if (night)
    return {
      src: '/assets/sky-moon.png',
      tint: 'var(--dim)',
      opacity: 0.5,
      style: mobile
        ? { top: '3vh', right: '6vw', width: 'min(10vh, 84px)', aspectRatio: '1' }
        : { top: '7vh', right: '9vw', width: 'min(18vh, 150px)', aspectRatio: '1' },
    };
  return {
    src: '/assets/sky-geese.png',
    tint: 'var(--faint)',
    opacity: 0.42,
    style: mobile ? { top: '2vh', left: 0, right: 0, height: '14vh' } : { top: '5vh', left: 0, right: 0, height: '30vh' },
  };
}

export function StageBackdrop({
  driving,
  reading,
  listeners,
  onToggleDriving,
  onToggleReading,
}: {
  driving?: boolean;
  reading?: boolean;
  listeners?: number;
  onToggleDriving?: () => void;
  onToggleReading?: () => void;
}): JSX.Element {
  const [isElectron] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('is-electron'),
  );
  const [now, setNow] = useState(() => new Date());
  const [weather, setWeather] = useState('');
  const [mobile, setMobile] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(max-width: 767px)').matches,
  );

  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(max-width: 767px)');
    const h = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      let token: string | null = null;
      try {
        token = localStorage.getItem('claudio.token');
      } catch {
        /* iOS private mode */
      }
      try {
        const r = await fetch('/api/env/summary', token ? { headers: { 'x-claudio-token': token } } : undefined);
        if (!r.ok) return;
        const data = (await r.json()) as { weather?: string };
        if (!cancelled && typeof data.weather === 'string') setWeather(data.weather.slice(0, 40));
      } catch {
        /* keep '' — line shows time only */
      }
    }
    load();
    const id = setInterval(load, 15 * 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const hour = now.getHours();
  const sky = useMemo(() => pickSky(weather, hour, mobile), [weather, hour, mobile]);
  const info = [weather, (listeners ?? 0) >= 2 ? `${listeners} 台在听` : ''].filter(Boolean).join(' · ');

  return (
    <div className="par-fg pointer-events-none absolute inset-0 z-[5] overflow-hidden" aria-hidden>
      <div
        className={`absolute${sky.drift ? ' sky-drift' : ''}`}
        style={{
          ...sky.style,
          background: sky.tint,
          opacity: sky.opacity,
          WebkitMaskImage: `url(${sky.src})`,
          maskImage: `url(${sky.src})`,
          WebkitMaskSize: '100% 100%',
          maskSize: '100% 100%',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
        }}
      />
      <div
        className={`absolute right-[max(1.25rem,env(safe-area-inset-right))] ${
          isElectron ? 'top-8' : 'top-[max(1.25rem,env(safe-area-inset-top))]'
        } font-display text-xs tracking-[0.15em] text-faint tabular-nums`}
      >
        {info}
        {info ? ' · ' : ''}
        <button
          onClick={onToggleReading}
          title={reading ? '读书中 — 点击结束' : '开启阅读模式'}
          className={`pointer-events-auto -mx-1 -my-3 px-2 py-3 font-display tracking-[0.15em] transition ${
            reading
              ? 'text-air'
              : 'opacity-50 hover:opacity-100 hover:underline hover:decoration-dotted hover:underline-offset-4'
          }`}
        >
          {reading ? '读书中' : '读书'}
        </button>
        {' · '}
        <button
          onClick={onToggleDriving}
          title={driving ? '行车中 — 点击结束' : '开启行车模式'}
          className={`pointer-events-auto -mx-1 -my-3 px-2 py-3 font-display tracking-[0.15em] transition ${
            driving
              ? 'text-seal'
              : 'opacity-50 hover:opacity-100 hover:underline hover:decoration-dotted hover:underline-offset-4'
          }`}
        >
          {driving ? '行车中' : '行车'}
        </button>
      </div>
    </div>
  );
}
